import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import { useDispatch, useSelector, batch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import {
  fetchHKTDCParticipantList,
  selectAllParticipants,
  resetParticipants,
  fetchNonAttendeeParticipants,
} from 'models/participants';
import { UserSessionContext } from 'components/UserSession';
import FooterV2 from 'components/footer_v2';
import BackToTopButton from 'components/BackToTopButton';
import { selectLocale } from 'models/localization';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import ParticipantListItem from '../participant/ParticipantListItem';

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 16px auto 32px;

  @media (max-width: 960px) {
    margin: 16px auto 32px;
    max-width: 860px;
    width: 100%;
  }

  @media (min-width: 960px) {
    margin: 0 auto 32px;
    width: 860px;
  }
`;

const HeaderText = styled.div`
  font-family: Lato;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 18px;
  letter-spacing: -0.408px;
  text-transform: capitalize;
  color: #484848;
  align-self: flex-start;
  padding: 16px 4px;
  background-color: #F0F2F5;

  @media (max-width: 960px) {
    padding-left: 20px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-items: center;
`;

const FetchButton = styled(Button)`
  margin: 8px auto;
`;

const FooterContainer = styled.div`
  position: fixed;
  text-align: center;
  bottom: 0;
  margin: 0 auto;
`;

const StyledFooterV2 = styled(FooterV2)`
  max-width: 100%;
`;

const StyledPaper = withStyles(() => ({
  root: {
    width: '100%',
    paddingTop: '4px',
    paddingBottom: '8px',
    marginBottom: '50px',
    overflow: 'auto',
  },
  rounded: {
    borderRadius: '8px',
  },
}))(Paper);

const ParticipantListContainer = ({ scrollableTarget }) => {
  const [isFetchError, setIsFetchError] = useState(false);
  const { userSession: { role } } = useContext(UserSessionContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const participants = useSelector(selectAllParticipants);
  const {
    initialLoading,
    hasNextPage,
    loading,
    totalCount,
  } = useSelector((state) => state.participants);
  const locale = useSelector(selectLocale);
  const { pageNames, features } = useSelector((s) => s.eventConfigs);
  const participantFilter = useSelector((state) => state.participantFilter);
  const filterOptions = useMemo(() => participantFilter?.get('queryFilters'), [participantFilter]);

  const fetchMore = useCallback(() => {
    dispatch((features.includes('hideAttendees') && role === 'attendee') ? fetchNonAttendeeParticipants() : fetchHKTDCParticipantList())
      .then((res) => {
        if (Object.keys(res?.payload?.entities || {}).length === 0) {
          setIsFetchError(true);
        }
      });
  }, [dispatch, features, role]);

  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    batch(() => {
      dispatch(resetParticipants());
      fetchMore();
    });
  }, []);

  useEffect(debounce(() => {
    batch(() => {
      dispatch(resetParticipants());
      fetchMore();
    });
  }, 1500), [filterOptions]);

  const header = useMemo(() => pageNames.participant?.[locale] || t('page.participants', 'Participants'), [locale, pageNames.participant, t]);

  function handleRetry() {
    setIsFetchError(false);
    fetchMore();
  }

  return (
    <RootContainer>
      <HeaderText>
        {header}
      </HeaderText>
      <StyledPaper>
        {
          (initialLoading) ? (
            <CircularLoadingIndicator size={32} />
          ) : (
            <InfiniteScroll
              dataLength={participants.length}
              next={fetchMore}
              hasMore={hasNextPage}
              loader={<CircularLoadingIndicator key="loading-indicator" size={32} />}
              scrollableTarget={scrollableTarget}
              scrollThreshold={1}
              style={{ overflow: 'hidden' }}
            >
              <div style={{ padding: '10px 16px' }}>
                {`${t('filter.total_filtered_results', 'Filtering results: %{total}', { total: totalCount })}`}
              </div>
              {
                participants.map((participant) => {
                  const {
                    firstName,
                    lastName,
                    name,
                    uuid,
                    uniqueToken,
                    customAttendeeFields: {
                      vexpo_chat_tag: fallbackTag,
                      vexpo_chat_user_description: fallbackDescription,
                    },
                    vexpoChatTag,
                    vexpoChatUserDescription,
                  } = participant;
                  return (
                    <ParticipantListItem
                      key={uuid}
                      uuid={uuid}
                      name={name}
                      firstName={firstName}
                      lastName={lastName}
                      userTag={vexpoChatTag || fallbackTag}
                      userDescription={vexpoChatUserDescription || fallbackDescription}
                      uniqueToken={uniqueToken}
                    />
                  );
                })
              }
            </InfiniteScroll>
          )
        }
        <ButtonRow>
          {hasNextPage && (
            <FetchButton
              variant="contained"
              color={isFetchError ? 'default' : 'primary'}
              onClick={handleRetry}
              disabled={initialLoading || loading}
            >
              {isFetchError ? 'retry' : 'next page'}
            </FetchButton>
          )}
        </ButtonRow>
      </StyledPaper>
      <FooterContainer>
        <StyledFooterV2 hashState="/participant" />
      </FooterContainer>
      <BackToTopButton scrollableTarget={scrollableTarget} />
    </RootContainer>
  );
};

ParticipantListContainer.propTypes = {
  scrollableTarget: PropTypes.instanceOf(Element),
};

ParticipantListContainer.defaultProps = {
  scrollableTarget: document.body,
};

export default ParticipantListContainer;
